import { fetchCatalog as apiFetchCatalog, fetchProbability as apiFetchProbability } from './riskApi';
import { normalizeCatalog, synthesizeCatalogFromPresentation, normalizeProbability } from './adapters';
import { normalizeRiskError } from './errors';

// Cache key deliberately excludes transient flags like forceRefetch so a
// retry can bypass the cache without evicting/duplicating the real entry.
// `op` IS included: the Drought Index </> toggle (arid only) asks a different
// question at the same threshold, and must never collide with the other
// direction's cached answer — mirrors the API's own Layer-2 cache key
// (risk/climatology.py#_result_cache_key).
export function cacheKey(p) {
    return [
        p.indicator,
        Number(p.lat).toFixed(4),
        Number(p.lon).toFixed(4),
        p.start_date,
        p.end_date,
        p.aggregation,
        p.threshold,
        p.units,
        p.op,
        // The Heat Index level and the minimum daily exposure, for the same reason as `op`.
        p.category,
        p.min_hours,
    ].join('|');
}

// Module-local abort controller — deliberately NOT the root store's shared
// activeRequestController (see store.js#startNewDataRequest). That one is
// aborted on every LOAD DATA click, which would cancel the very risk
// request that same click just triggered.
let activeRiskController = null;
let requestSeq = 0;

// Cancels whatever request is in flight and returns a fresh sequence number for
// the CURRENT call — invoked unconditionally at the top of fetchProbability,
// cache hit or miss. Fixes the race where switching indicators quickly (or
// flipping the Drought Index direction) showed one indicator's numbers under
// another's chip: a cache hit used to return early WITHOUT aborting the
// in-flight request or bumping the sequence, so a still-in-flight response for
// the indicator switched away from could land later and overwrite the cached
// view that had already replaced it on screen.
function supersede() {
    if (activeRiskController) activeRiskController.abort();
    activeRiskController = null;
    return ++requestSeq;
}

export default {
    fetchCatalog({ state, commit }) {
        if (state.catalogStatus === 'ready' || state.catalogStatus === 'loading') return Promise.resolve();
        commit('SET_CATALOG_STATUS', 'loading');
        return apiFetchCatalog({})
            .then((response) => {
                commit('SET_CATALOG', normalizeCatalog(response.data));
            })
            .catch((err) => {
                // eslint-disable-next-line no-console
                console.error('[risk] GET /risk failed — falling back to a presentation-only catalog:', err);
                commit('SET_CATALOG_ERROR', normalizeRiskError(err, {}));
                commit('SET_CATALOG', synthesizeCatalogFromPresentation());
            });
    },

    fetchProbability({ state, commit }, params) {
        const key = cacheKey(params);
        const cached = !params.forceRefetch ? state.cache[key] : null;

        commit('SET_LAST_PARAMS', params);
        const mySeq = supersede();

        if (cached) {
            if (cached.error) {
                commit('SET_ERROR', cached.error);
                commit('SET_STATUS', 'error');
            } else {
                commit('SET_DATA', cached.data);
                commit('SET_STATUS', cached.data.warnings.noData ? 'empty' : 'ready');
            }
            return Promise.resolve();
        }

        activeRiskController = new AbortController();
        commit('SET_STATUS', 'loading');

        // forceRefetch is ours, not the API's: axios serializes every key in `params`
        // into the query string, so passing it through added a bogus
        // ?forceRefetch=true to every retried request (and, via SET_LAST_PARAMS, to
        // every retry after that).
        // eslint-disable-next-line no-unused-vars
        const { forceRefetch, ...query } = params;

        return apiFetchProbability(query, { signal: activeRiskController.signal })
            .then((response) => {
                if (mySeq !== requestSeq) return; // superseded by a newer request
                // Belt-and-suspenders alongside the sequence check above: this asks
                // "is this response for the data currently on screen", not just "is
                // this the newest dispatch" — semantically the right question, and it
                // keeps holding even if the sequence counter is ever refactored away.
                if (key !== cacheKey(state.lastParams)) return;
                const data = normalizeProbability(response.data);
                commit('CACHE_PUT', { key, entry: { data, error: null } });
                commit('SET_DATA', data);
                commit('SET_STATUS', data.warnings.noData ? 'empty' : 'ready');
            })
            .catch((err) => {
                const normalized = normalizeRiskError(err, params);
                if (normalized === null) return; // canceled — a newer request is in flight
                if (mySeq !== requestSeq) return;
                if (key !== cacheKey(state.lastParams)) return;
                commit('CACHE_PUT', { key, entry: { data: null, error: normalized } });
                commit('SET_ERROR', normalized);
                commit('SET_STATUS', 'error');
            });
    },

    retry({ state, dispatch }) {
        if (!state.lastParams) return Promise.resolve();
        return dispatch('fetchProbability', { ...state.lastParams, forceRefetch: true });
    },
};
